import { client, gql } from 'data-provider';
import { CustomUploadedFile, IPayment, IPurchase, IPurchaseStockCorrection } from 'interfaces';

export interface IAllPurchaseQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  start?: number;
  limit?: number;
}

interface CreatePurchaseData {
  note?: string;
  supplier: string;
  supplierPoc?: string;
  purchaseAddress: string;
  purchaseDate: Date;
  purchaseStatus: string;
  warehouse: string;
  vatAmount?: number;
  shipmentCost?: number;
  discountCost?: number;
  purchaseProducts: {
    sku: string;
    unitPrice: number;
    quantity: number;
  }[];
  purchaseExpense: {
    id: string;
  }[];
  purchasePayments: IPayment[];
  transactionToken: string;
}

type CreatePurchaseStockCorrectionData = Omit<
  CreatePurchaseData,
  'purchaseProducts' | 'purchaseExpense' | 'purchasePayments'
> & {
  stockCorrectionPurchaseProducts: {
    sku: string;
    unitPrice: number;
    quantity: number;
  }[];
};

export const getPurchases = async (purchaseQuery?: IAllPurchaseQuery) => {
  const where = {
    query: purchaseQuery && purchaseQuery.searchString ? purchaseQuery.searchString : '',
    startDate: purchaseQuery && purchaseQuery.startDate ? purchaseQuery.startDate : undefined,
    endDate: purchaseQuery && purchaseQuery.endDate ? purchaseQuery.endDate : undefined,
  };
  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      purchaseCopies(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          purchaseDate
          purchaseStatus
          totalAmount
          supplier {
            name
            mobileNumber
          }
          createdBy {
            name
            email
            id
            mobileNumber
          }
          warehouse {
            name
            id
          }
          purchaseProductsCopy {
            id
            quantity
            unitPrice
            subTotal
            productVariant {
              id
              name
              sku
            }
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: purchaseQuery && purchaseQuery.start ? purchaseQuery.start : 1,
    limit: purchaseQuery && purchaseQuery.limit ? purchaseQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.purchaseCopies as {
    data: IPurchase[];
    page: number;
    size: number;
    total: number;
  };
};

export const getPurchase = async (id: string) => {
  const query = gql`
    query ($id: ID!) {
      purchase(id: $id) {
        id
        purchaseAddress
        purchaseDate
        purchaseStatus
        vatAmount
        shipmentCost
        totalAmount
        note
        supplier {
          id
          name
        }
        purchaseProducts {
          id
          unitPrice
          quantity
          subTotal
          product {
            id
            sku
            name
          }
          supplierPoc {
            id
            name
          }
          warehouse {
            id
          }
        }
      }
    }
  `;

  const variables = { id };
  const data = await client.request(query, variables);
  return data.purchase as IPurchase;
};

export const createPurchase = async (data: CreatePurchaseData) => {
  const createPurchase = gql`
    mutation ($input: createPurchaseCopyInput!) {
      createPurchaseCopy(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(createPurchase, variables);
  return createdData.createPurchaseCopy as { id: string };
};

export const updatePurchaseStatus = async (
  id: string,
  purchaseStatus: string,
  receipts?: CustomUploadedFile[]
) => {
  const updatePurchaseStatus = gql`
    mutation ($input: updatePurchaseCopyStatusInput) {
      updatePurchaseCopyStatus(input: $input) {
        purchaseCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { purchaseStatus, receipts },
    },
  };
  const updatedData = await client.request(updatePurchaseStatus, variables);
  return updatedData;
};

export const deletePurchase = async (id: string) => {
  const deletePurchase = gql`
    mutation ($input: deletePurchaseCopyInput!) {
      deletePurchaseCopy(input: $input) {
        purchaseCopy {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deletePurchase, variables);
};

export const getPurchasesDropdown = async (startDate?: string, endDate?: string) => {
  const where = { startDate, endDate };

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      purchaseCopyDropdown(where: $where, sort: $sort, start: $start, limit: $limit) {
        id
        purchaseAddress
        purchaseDate
        purchaseStatus
        vatAmount
        shipmentCost
        totalAmount
        note
        supplier {
          id
          name
          mobileNumber
        }
      }
    }
  `;

  const variables = { where };

  const data = await client.request(query, variables);
  return data.purchaseCopyDropdown as IPurchase[];
};

export const getPurchaseStockCorrections = async (purchaseQuery?: IAllPurchaseQuery) => {
  const where = {
    query: purchaseQuery && purchaseQuery.searchString ? purchaseQuery.searchString : '',
    startDate: purchaseQuery && purchaseQuery.startDate ? purchaseQuery.startDate : undefined,
    endDate: purchaseQuery && purchaseQuery.endDate ? purchaseQuery.endDate : undefined,
  };

  const query = gql`
    query ($sort: SortInput, $where: JSON, $start: Int, $limit: Int) {
      stockCorrectionPurchases(sort: $sort, where: $where, start: $start, limit: $limit) {
        data {
          id
          purchaseDate
          purchaseStatus
          totalAmount
          supplier {
            name
            mobileNumber
          }
          createdBy {
            name
            email
            id
            mobileNumber
          }
          warehouse {
            name
            id
          }
          stockCorrectionPurchaseProducts {
            id
            quantity
            unitPrice
            subTotal
            productVariant {
              id
              name
              sku
            }
          }
        }
        total
        size
        page
      }
    }
  `;

  const variables = {
    where,
    start: purchaseQuery && purchaseQuery.start ? purchaseQuery.start : 1,
    limit: purchaseQuery && purchaseQuery.limit ? purchaseQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.stockCorrectionPurchases as {
    data: IPurchaseStockCorrection[];
    page: number;
    size: number;
    total: number;
  };
};

export const createPurchaseStockCorrection = async (data: CreatePurchaseStockCorrectionData) => {
  const createPurchase = gql`
    mutation ($input: createStockCorrectionPurchaseInput!) {
      createStockCorrectionPurchase(input: $input) {
        id
      }
    }
  `;

  const variables = {
    input: { data },
  };

  const createdData = await client.request(createPurchase, variables);
  return createdData.createStockCorrectionPurchase as { id: string };
};

export const updatePurchaseStockCorrectionStatus = async (id: string, purchaseStatus: string) => {
  const updatePurchaseStatus = gql`
    mutation ($input: updateStockCorrectionPurchaseStatusInput) {
      updateStockCorrectionPurchaseStatus(input: $input) {
        stockCorrectionPurchase {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
      data: { purchaseStatus },
    },
  };
  const updatedData = await client.request(updatePurchaseStatus, variables);
  return updatedData;
};

export const deletePurchaseStockCorrection = async (id: string) => {
  const deletePurchase = gql`
    mutation ($input: deleteStockCorrectionPurchaseInput!) {
      deleteStockCorrectionPurchase(input: $input) {
        stockCorrectionPurchase {
          id
        }
      }
    }
  `;

  const variables = {
    input: {
      where: { id },
    },
  };

  await client.request(deletePurchase, variables);
};
